import { ref, computed, watch, type Ref, type ComputedRef } from 'vue';
import { MapAreaService } from '@/modules/map-editor';

const LAYERS_MAX = 12;
const ZOOM_FACTOR = 0.05714;

interface UseZoomManagerReturn {
  /** Current zoom level combining device pixel ratio and zoom factor. */
  zoomLevel: ComputedRef<number>;
  /** Cancel any running zoom animation (call on unmount). */
  cancelZoomAnimation: () => void;
}

/**
 * Composable for managing zoom functionality.
 * Handles layer-based zooming and smooth animations between layers.
 *
 * @param layersAmount - Reactive ref for total number of layers
 * @param layerCurrentID - Reactive ref for current layer ID (1-based)
 * @param mapAreaService - MapAreaService singleton instance
 * @returns Zoom level ref and cleanup function
 */
export function useZoomManager(
  layersAmount: Ref<number>,
  layerCurrentID: Ref<number>,
  mapAreaService: MapAreaService
): UseZoomManagerReturn {
  const zoomFactor = ref(1);
  const isZooming = ref(false);
  let zoomAnimationFrameId: number | null = null;

  /**
   * Zoom multiply factor derived from layer count.
   * Adjusts zoom step size based on total layers available.
   */
  const zoomMultiply = computed(() =>
    parseFloat((ZOOM_FACTOR * (LAYERS_MAX / layersAmount.value)).toFixed(5))
  );

  /**
   * Current zoom level combining device pixel ratio and zoom factor.
   */
  const zoomLevel = computed(() =>
    window.devicePixelRatio * zoomFactor.value
  );

  // Watch layer amount changes — recalculate zoom factor when layers are added/removed
  watch(layersAmount, (newVal) => {
    if (layerCurrentID.value !== 1) {
      zoomFactor.value = parseFloat(
        (1 + (ZOOM_FACTOR * (LAYERS_MAX / newVal)) * (layerCurrentID.value - 1)).toFixed(5)
      );
      mapAreaService.updateMap(true);
    }
  });

  // Watch current layer change — animate zoom transition
  watch(layerCurrentID, (newVal, oldVal) => {
    if (oldVal === undefined) return;
    animateZoom(oldVal - 1, newVal - 1, 200);
  });

  /**
   * Animate zoom transition between two layer indices.
   *
   * @param previousLayerIndex - Zero-based index of the previous layer
   * @param currentLayerIndex - Zero-based index of the target layer
   * @param duration - Animation duration in milliseconds
   */
  function animateZoom(
    previousLayerIndex: number,
    currentLayerIndex: number,
    duration: number = 200
  ): void {
    if (previousLayerIndex === currentLayerIndex) return;

    // Cancel any in-progress zoom animation
    if (zoomAnimationFrameId !== null) {
      cancelAnimationFrame(zoomAnimationFrameId);
      zoomAnimationFrameId = null;
    }

    const fps = 60;
    const millisecondsToFrame = 1000 / fps;
    const stepAmount = Math.floor(duration / millisecondsToFrame);
    let cycleStep = 0;

    const stepValue = (
      currentLayerIndex * zoomMultiply.value -
      previousLayerIndex * zoomMultiply.value
    ) / stepAmount;

    isZooming.value = true;
    mapAreaService.updateMap(true);

    function zoomStep(): void {
      if (cycleStep === stepAmount) {
        zoomFactor.value = parseFloat(
          (1 + zoomMultiply.value * currentLayerIndex).toFixed(5)
        );
        isZooming.value = false;
        zoomAnimationFrameId = null;
        mapAreaService.updateMap(false);
      } else {
        zoomAnimationFrameId = requestAnimationFrame(zoomStep);
        zoomFactor.value += stepValue;
        cycleStep++;
      }
    }

    zoomStep();
  }

  /**
   * Cancel any running zoom animation (call on unmount).
   */
  function cancelZoomAnimation(): void {
    if (zoomAnimationFrameId !== null) {
      cancelAnimationFrame(zoomAnimationFrameId);
      zoomAnimationFrameId = null;
    }
    isZooming.value = false;
  }

  return {
    zoomLevel,
    cancelZoomAnimation
  };
}
